import {Auth, Storage} from 'aws-amplify'
import {getLogger} from '@/shared/utils'
import {getShareFileUrl, sharePathOfObjectUri, type ShareRoot} from '../../core/storage'
import {plainFileFormat} from './plain-file-formats'

/**
 * The getApiUrl payload a storage-backed viewer expects: a presigned URL plus how to read the bytes.
 * The `format` is resolved by the OBserver (it rides on the /metadata response) — the frontend no longer
 * guesses it from the file extension. `compressed` is a stream-decoding detail derived from the key.
 */
export interface StorageViewerSource {
  /** Presigned HTTPS URL the browser fetches (with Range) directly from storage. */
  value: string
  /**
   * Format id the viewer's parser uses (e.g. fasta/vcf/tsv). Comes from the OBserver metadata, falling
   * back to the file name when that carries nothing usable (see {@link usableFormat}).
   */
  format: string
  /** Whether the payload is gzip-compressed (streamed via DecompressionStream). */
  compressed: boolean
  /**
   * This host answers `resolveDocumentResources`: it signs the images a Markdown file references
   * (see `document-resources.ts`). A viewer asks only when this is set, so it never waits on a host
   * that predates it.
   */
  documentResources: true
  /**
   * This host answers `setPrintTitle`: it shows the document's title as the page title while the
   * viewer's print dialog is open, so the saved PDF is named after the file (see `print-title.ts`).
   */
  printTitle: true
}

/**
 * The format id the OBserver uses for a raw file it could not name (see MetadataHelper
 * `createRawMetadataResponse`). Carries no information for a viewer, so it is treated as absent.
 */
const UNRESOLVED_FORMAT = 'raw'

/**
 * A format id the viewer can actually branch on.
 *
 * The format normally rides on the config (`objectInfo.type`), but it is not guaranteed to be there:
 * the OBserver answers `raw` when the /metadata request carried no format id, and a tab restored from
 * an older localStorage entry can carry no type at all. Sending the payload as-is is worse than it
 * looks — `html-viewer` decides a reply is storage-backed *by* the presence of a format, so a missing
 * one silently drops it back onto the OBserver controller protocol and it asks the presigned URL for
 * `/info`. Storage answers 403 (the signature covers the object path, not `<object>/info`), which
 * reads like an expired-credentials problem and hides the actual cause.
 *
 * For the formats this affects — png/jpeg/gif/webp/bmp/svg/pdf/txt, plus the bioinformatics ids the
 * bioinfo-viewer keys on — the extension *is* the answer, so derive it instead. `txt` is the last
 * resort for an extension-less file: html-viewer renders it, which beats a 403.
 */
function usableFormat(format: string | undefined, key: string): string {
  const declared = format?.trim().toLowerCase()
  if (declared && declared !== UNRESOLVED_FORMAT) {
    return declared
  }
  // The `.gz` suffix says how the bytes are packed, not what they are, and rides on `compressed`
  // instead: `counts.tsv.gz` is a tsv.
  const named = key.replace(/\.gz$/i, '')
  const derived = plainFileFormat(named) ?? extensionOf(named) ?? 'txt'
  getLogger().warn(
    `Storage viewer source for "${key}" had no usable format (${JSON.stringify(format)}); using "${derived}" derived from the file name.`
  )
  return derived
}

/** Lower-cased extension of a storage key, or undefined for a dotfile / extension-less name. */
function extensionOf(key: string): string | undefined {
  const basename = key.substring(key.lastIndexOf('/') + 1)
  const dot = basename.lastIndexOf('.')
  return dot > 0 ? basename.substring(dot + 1).toLowerCase() : undefined
}

/**
 * Split an objectUri produced by getFileS3Url (`s3://<bucket>/<identityId>/<key>`) into the identity
 * whose space it is in and the bare key below it, i.e. the file name Amplify Storage.get expects with
 * the private/customPrefix scheme used throughout the app (see core/_requests.ts downloadFile).
 */
export function splitObjectUri(objectUri: string): {identityId: string; key: string} {
  const withoutScheme = objectUri.replace(/^s3:\/\//, '')
  const segments = withoutScheme.split('/')
  // [0] bucket, [1] poolId/identityId, rest = file key (may contain subfolders)
  return {
    identityId: decodeURIComponent(segments[1] ?? ''),
    key: decodeURIComponent(segments.slice(2).join('/')),
  }
}

/** A Cognito identity id, `<region>:<uuid>`: what names a user's space in the bucket. */
const IDENTITY_ID = /^[a-z]{2}(-[a-z]+)+-\d:[0-9a-f-]{36}$/

/**
 * Presign a key in the user's own space. Amplify's private level signs `<the caller's identity>/<key>`
 * whatever identity the URI names, so a URI in someone else's space would come back as a URL for a
 * different file of the user's own that happens to share the path. Refuse that instead. (Only a URI
 * that names a real identity is checked: the key was all that counted before, and an odd URI from
 * an older tab still opens as it did.)
 */
export async function presignOwnKey(objectUri: string): Promise<{value: string; key: string}> {
  const {identityId, key} = splitObjectUri(objectUri)
  if (IDENTITY_ID.test(identityId)) {
    const credentials = await Auth.currentCredentials()
    if (credentials?.identityId && identityId !== credentials.identityId) {
      throw new Error('This file is in another user\'s storage and was not opened from a shared folder.')
    }
  }
  const presigned = await Storage.get(key, {
    customPrefix: {public: '', private: '', protected: ''},
    level: 'private',
    download: false,
  })
  return {value: typeof presigned === 'string' ? presigned : '', key}
}

/**
 * Presign a file in a folder someone else shares, through the sharing API (a fresh one-folder
 * session per URL, E11). The URI must name a file inside that folder.
 */
export async function presignSharedKey(objectUri: string, share: ShareRoot): Promise<{value: string; key: string}> {
  const path = sharePathOfObjectUri(objectUri, share)
  if (path === undefined) {
    throw new Error('This file is not inside the shared folder it was opened from.')
  }
  return {value: await getShareFileUrl(share.id, path), key: path}
}

/**
 * Resolve the {@link StorageViewerSource} for a storage-backed viewer: mint a presigned URL for the
 * file the OBserver pointed us at, and pass through the OBserver-resolved `format`. This is generic —
 * any viewer whose metadata declares `dataSource === STORAGE` uses it; nothing here is specific to a
 * particular viewer.
 *
 * The user's own files are signed with the app's proven Storage.get private/customPrefix pattern.
 * A file opened from a folder someone else shares (`share`) is signed by the sharing API instead,
 * and only if the URI really is inside that folder.
 *
 * @param format the OBserver-resolved format; falls back to the file name when it is missing or
 *               unresolved (see {@link usableFormat})
 * @param share  the shared folder the viewer was opened from, if any (the config's `share`)
 */
export async function resolveStorageSource(
  objectUri: string,
  format: string | undefined,
  share?: ShareRoot
): Promise<StorageViewerSource> {
  const {value, key} = share
    ? await presignSharedKey(objectUri, share)
    : await presignOwnKey(objectUri)
  const compressed = key.toLowerCase().endsWith('.gz')
  return {value, format: usableFormat(format, key), compressed, documentResources: true, printTitle: true}
}
